import React, { createContext, useContext, useState, ReactNode } from 'react';

export type AlertType = 'success' | 'error' | 'info';

interface Alert {
    id: string;
    message: string;
    type: AlertType;
    persistent?: boolean;
}

interface AlertContextType {
    alerts: Alert[];
    showAlert: (message: string, type?: AlertType, duration?: number, onTimeout?: () => void, persistent?: boolean) => void;
    clearAlert: (id: string) => void;
    clearAllAlerts: () => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const useAlert = (): AlertContextType => {
    const context = useContext(AlertContext);
    if (!context) {
        throw new Error('useAlert must be used within an AlertProvider');
    }
    return context;
};

interface AlertProviderProps {
    children: ReactNode;
}

export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
    const [alerts, setAlerts] = useState<Alert[]>([]);

    const showAlert = (message: string, type: AlertType = 'info', duration: number = 5000, onTimeout?: () => void, persistent = false): void => {
        const id = Math.random().toString(36).substring(2, 15);
        const newAlert: Alert = { id, message, type, persistent };

        setAlerts(prev => [...prev, newAlert]);

        // Persistent alerts must be dismissed manually – skip the auto-dismiss timer
        if (!persistent) {
            setTimeout(() => {
                clearAlert(id);
                if (onTimeout) {
                    onTimeout();
                }
            }, duration);
        }
    };

    const clearAlert = (id: string): void => {
        setAlerts(prev => prev.filter(alert => alert.id !== id));
    };

    const clearAllAlerts = (): void => {
        setAlerts([]);
    };

    const value: AlertContextType = {
        alerts,
        showAlert,
        clearAlert,
        clearAllAlerts,
    };

    return <AlertContext.Provider value={value}>{children}</AlertContext.Provider>;
};